/**
 * Internal Missions (البعثات الداخلية)
 * Equivalent rates (last-year accepted minimums, أقل معدل) sourced from
 * equalvant_rate/internal_missions/2027/internal-.pdf (2026/2027 admission
 * results, bachelor pages 2/8–5/8; علمي/أدبي columns per specialty).
 *
 * IM surfaces a single threshold concept: last year's lowest accepted value.
 * The scholarship plan's minimum-to-apply floors are intentionally NOT
 * modeled (removed Aug 2026 by request) — nothing gates the calculation;
 * أقل معدل is shown as guidance on cards, the score step, and results.
 *
 * Structure: FM-style flat top-level. Each specialty is a "College".
 * Institutions are stored as Department rows (via `departmentKey`).
 * One row per (collegeKey, departmentKey, track).
 *   - Engineering specialties: scientific only, carry an equivalent rate
 *     (معدل مكافئ in the rates PDF).
 *   - Non-engineering specialties: up to two rows (scientific + literary)
 *     with type "high_school"; equivalentRate holds the HS cutoff. Rows
 *     marked علمي فقط in the PDF have no literary row.
 *
 * Specialties absent from the 2026/2027 rates PDF (AUK economics/marketing/HR,
 * GUST·AUM·AIU marketing, AUM/AIU chemical engineering, AU event management)
 * keep their previous values — same keep-visible convention as the plan drop.
 * Diploma institutions (rates PDF pages 6/8–8/8) are out of scope: the wizard
 * models the bachelor program only.
 */

import type { College, CollegeWeights, Department, Gender, Scores, StepAlert, StepAlertLine, Track } from "./sharedTypes";

// ── Weight templates ────────────────────────────────────────────────

const engineeringWeights: CollegeWeights = {
    highSchoolWeight: 65,
    arabicWeight: 0,
    chemistryWeight: 0,
    mathWeight: 20,
    englishWeight: 15,
    minHighSchool: null,
    minEnglish: null,
    admissionType: "equivalent",
};

const defaultWeights: CollegeWeights = {
    highSchoolWeight: 100,
    arabicWeight: 0,
    chemistryWeight: 0,
    mathWeight: 0,
    englishWeight: 0,
    minHighSchool: null,
    minEnglish: null,
    admissionType: "high_school",
};

// ── Department rows ─────────────────────────────────────────────────
// Code naming: IM-<SPECIALTY_CODE>-<INSTITUTION_CODE>[-S|-L]
// Institution codes: AUK, KILAW, GUST, AU, AUM, KCST, AIU

export const departments: Department[] = [
    // ── Engineering (scientific only) ──────────────────────────────

    { code: "IM-ARCH-AUM",  collegeKey: "architectural_engineering",     departmentKey: "american_university_middle_east",      equivalentRate: 74.3055, type: "equivalent", track: "scientific" },

    { code: "IM-BME-AIU",   collegeKey: "biomedical_engineering",         departmentKey: "american_international_university",    equivalentRate: 75.9895, type: "equivalent", track: "scientific" },

    { code: "IM-CHE-AUM",   collegeKey: "chemical_engineering",           departmentKey: "american_university_middle_east",      equivalentRate: 60.09, type: "equivalent", track: "scientific" },
    { code: "IM-CHE-AIU",   collegeKey: "chemical_engineering",           departmentKey: "american_international_university",    equivalentRate: 80.25, type: "equivalent", track: "scientific" },

    { code: "IM-CVE-AUM",   collegeKey: "civil_engineering",              departmentKey: "american_university_middle_east",      equivalentRate: 74.6665, type: "equivalent", track: "scientific" },
    { code: "IM-CVE-AIU",   collegeKey: "civil_engineering",              departmentKey: "american_international_university",    equivalentRate: 71.9515, type: "equivalent", track: "scientific" },

    { code: "IM-CPE-AUK",   collegeKey: "computer_engineering",           departmentKey: "american_university_kuwait",           equivalentRate: 67.549, type: "equivalent", track: "scientific" },
    { code: "IM-CPE-GUST",  collegeKey: "computer_engineering",           departmentKey: "gulf_university",                      equivalentRate: 73.3405, type: "equivalent", track: "scientific" },
    { code: "IM-CPE-AUM",   collegeKey: "computer_engineering",           departmentKey: "american_university_middle_east",      equivalentRate: 77.1115, type: "equivalent", track: "scientific" },
    { code: "IM-CPE-KCST",  collegeKey: "computer_engineering",           departmentKey: "kuwait_college_science_technology",    equivalentRate: 68.4275, type: "equivalent", track: "scientific" },

    { code: "IM-ECE-AIU",   collegeKey: "electrical_computer_engineering",departmentKey: "american_international_university",    equivalentRate: 72.954, type: "equivalent", track: "scientific" },

    { code: "IM-ELE-AUK",   collegeKey: "electrical_engineering",         departmentKey: "american_university_kuwait",           equivalentRate: 66.53, type: "equivalent", track: "scientific" },
    { code: "IM-ELE-GUST",  collegeKey: "electrical_engineering",         departmentKey: "gulf_university",                      equivalentRate: 71.5615, type: "equivalent", track: "scientific" },
    { code: "IM-ELE-AUM",   collegeKey: "electrical_engineering",         departmentKey: "american_university_middle_east",      equivalentRate: 76.3075, type: "equivalent", track: "scientific" },

    { code: "IM-ETC-KCST",  collegeKey: "electronics_comm_engineering",   departmentKey: "kuwait_college_science_technology",    equivalentRate: 59.02, type: "equivalent", track: "scientific" },

    { code: "IM-SYS-AUK",   collegeKey: "systems_engineering",            departmentKey: "american_university_kuwait",           equivalentRate: 55.25, type: "equivalent", track: "scientific" },

    // ── Non-engineering (both tracks where PDF provides both) ─────

    // Computer Science (scientific only at every institution)
    { code: "IM-CSC-AUK-S",  collegeKey: "computer_science", departmentKey: "american_university_kuwait",        equivalentRate: 77.78, type: "high_school", track: "scientific" },
    { code: "IM-CSC-GUST-S", collegeKey: "computer_science", departmentKey: "gulf_university",                   equivalentRate: 81.61, type: "high_school", track: "scientific" },
    { code: "IM-CSC-KCST-S", collegeKey: "computer_science", departmentKey: "kuwait_college_science_technology", equivalentRate: 78.53, type: "high_school", track: "scientific" },
    { code: "IM-CSC-AIU-S",  collegeKey: "computer_science", departmentKey: "american_international_university", equivalentRate: 84.74, type: "high_school", track: "scientific" },

    // Media
    { code: "IM-MDA-AUK-S",  collegeKey: "media", departmentKey: "american_university_kuwait", equivalentRate: 86.98, type: "high_school", track: "scientific" },
    { code: "IM-MDA-AUK-L",  collegeKey: "media", departmentKey: "american_university_kuwait", equivalentRate: 85.00, type: "high_school", track: "literary"   },
    { code: "IM-MDA-GUST-S", collegeKey: "media", departmentKey: "gulf_university",            equivalentRate: 85.13, type: "high_school", track: "scientific" },
    { code: "IM-MDA-GUST-L", collegeKey: "media", departmentKey: "gulf_university",            equivalentRate: 86.60, type: "high_school", track: "literary"   },

    // Economics
    { code: "IM-ECO-AUK-S", collegeKey: "economics", departmentKey: "american_university_kuwait", equivalentRate: 83.40, type: "high_school", track: "scientific" },
    { code: "IM-ECO-AUK-L", collegeKey: "economics", departmentKey: "american_university_kuwait", equivalentRate: 78.00, type: "high_school", track: "literary"   },

    // Management
    { code: "IM-MGT-AUK-S", collegeKey: "management", departmentKey: "american_university_kuwait",        equivalentRate: 80.18, type: "high_school", track: "scientific" },
    { code: "IM-MGT-AUK-L", collegeKey: "management", departmentKey: "american_university_kuwait",        equivalentRate: 82.00, type: "high_school", track: "literary"   },
    { code: "IM-MGT-AIU-S", collegeKey: "management", departmentKey: "american_international_university", equivalentRate: 79.86, type: "high_school", track: "scientific" },
    { code: "IM-MGT-AIU-L", collegeKey: "management", departmentKey: "american_international_university", equivalentRate: 80.10, type: "high_school", track: "literary"   },

    // International Relations
    { code: "IM-IRE-AUK-S", collegeKey: "international_relations", departmentKey: "american_university_kuwait", equivalentRate: 80.40, type: "high_school", track: "scientific" },
    { code: "IM-IRE-AUK-L", collegeKey: "international_relations", departmentKey: "american_university_kuwait", equivalentRate: 80.98, type: "high_school", track: "literary"   },

    // Accounting
    { code: "IM-ACC-AUK-S",  collegeKey: "accounting", departmentKey: "american_university_kuwait",        equivalentRate: 82.24, type: "high_school", track: "scientific" },
    { code: "IM-ACC-AUK-L",  collegeKey: "accounting", departmentKey: "american_university_kuwait",        equivalentRate: 80.94, type: "high_school", track: "literary"   },
    { code: "IM-ACC-GUST-S", collegeKey: "accounting", departmentKey: "gulf_university",                   equivalentRate: 80.00, type: "high_school", track: "scientific" },
    { code: "IM-ACC-GUST-L", collegeKey: "accounting", departmentKey: "gulf_university",                   equivalentRate: 80.00, type: "high_school", track: "literary"   },
    { code: "IM-ACC-AUM-S",  collegeKey: "accounting", departmentKey: "american_university_middle_east",   equivalentRate: 81.46, type: "high_school", track: "scientific" },
    { code: "IM-ACC-AUM-L",  collegeKey: "accounting", departmentKey: "american_university_middle_east",   equivalentRate: 81.84, type: "high_school", track: "literary"   },
    { code: "IM-ACC-AIU-S",  collegeKey: "accounting", departmentKey: "american_international_university", equivalentRate: 81.25, type: "high_school", track: "scientific" },
    { code: "IM-ACC-AIU-L",  collegeKey: "accounting", departmentKey: "american_international_university", equivalentRate: 81.20, type: "high_school", track: "literary"   },

    // Management Information Systems (MIS)
    { code: "IM-MIS-AUK-S",   collegeKey: "management_info_systems", departmentKey: "american_university_kuwait",      equivalentRate: 75.00, type: "high_school", track: "scientific" },
    { code: "IM-MIS-AUK-L",   collegeKey: "management_info_systems", departmentKey: "american_university_kuwait",      equivalentRate: 78.49, type: "high_school", track: "literary"   },
    { code: "IM-MIS-GUST-S",  collegeKey: "management_info_systems", departmentKey: "gulf_university",                 equivalentRate: 76.76, type: "high_school", track: "scientific" },
    { code: "IM-MIS-GUST-L",  collegeKey: "management_info_systems", departmentKey: "gulf_university",                 equivalentRate: 78.07, type: "high_school", track: "literary"   },
    { code: "IM-MIS-AU-S",    collegeKey: "management_info_systems", departmentKey: "australian_university",           equivalentRate: 75.10, type: "high_school", track: "scientific" },
    { code: "IM-MIS-AU-L",    collegeKey: "management_info_systems", departmentKey: "australian_university",           equivalentRate: 78.75, type: "high_school", track: "literary"   },
    { code: "IM-MIS-AUM-S",   collegeKey: "management_info_systems", departmentKey: "american_university_middle_east", equivalentRate: 78.20, type: "high_school", track: "scientific" },
    { code: "IM-MIS-AUM-L",   collegeKey: "management_info_systems", departmentKey: "american_university_middle_east", equivalentRate: 78.15, type: "high_school", track: "literary"   },

    // Graphic Design
    { code: "IM-GRA-AUK-S", collegeKey: "graphic_design", departmentKey: "american_university_kuwait",        equivalentRate: 78.74, type: "high_school", track: "scientific" },
    { code: "IM-GRA-AUK-L", collegeKey: "graphic_design", departmentKey: "american_university_kuwait",        equivalentRate: 83.21, type: "high_school", track: "literary"   },
    { code: "IM-GRA-AIU-S", collegeKey: "graphic_design", departmentKey: "american_international_university", equivalentRate: 82.00, type: "high_school", track: "scientific" },
    { code: "IM-GRA-AIU-L", collegeKey: "graphic_design", departmentKey: "american_international_university", equivalentRate: 82.13, type: "high_school", track: "literary"   },

    // Finance
    { code: "IM-FIN-AUK-S",  collegeKey: "finance", departmentKey: "american_university_kuwait",        equivalentRate: 78.64, type: "high_school", track: "scientific" },
    { code: "IM-FIN-AUK-L",  collegeKey: "finance", departmentKey: "american_university_kuwait",        equivalentRate: 80.58, type: "high_school", track: "literary"   },
    { code: "IM-FIN-GUST-S", collegeKey: "finance", departmentKey: "gulf_university",                   equivalentRate: 79.96, type: "high_school", track: "scientific" },
    { code: "IM-FIN-GUST-L", collegeKey: "finance", departmentKey: "gulf_university",                   equivalentRate: 80.05, type: "high_school", track: "literary"   },
    { code: "IM-FIN-AUM-S",  collegeKey: "finance", departmentKey: "american_university_middle_east",   equivalentRate: 78.06, type: "high_school", track: "scientific" },
    { code: "IM-FIN-AUM-L",  collegeKey: "finance", departmentKey: "american_university_middle_east",   equivalentRate: 80.14, type: "high_school", track: "literary"   },
    { code: "IM-FIN-AIU-S",  collegeKey: "finance", departmentKey: "american_international_university", equivalentRate: 78.32, type: "high_school", track: "scientific" },
    { code: "IM-FIN-AIU-L",  collegeKey: "finance", departmentKey: "american_international_university", equivalentRate: 80.82, type: "high_school", track: "literary"   },

    // Marketing
    { code: "IM-MKT-AUK-S",  collegeKey: "marketing", departmentKey: "american_university_kuwait",        equivalentRate: 80.75, type: "high_school", track: "scientific" },
    { code: "IM-MKT-AUK-L",  collegeKey: "marketing", departmentKey: "american_university_kuwait",        equivalentRate: 81.77, type: "high_school", track: "literary"   },
    { code: "IM-MKT-GUST-S", collegeKey: "marketing", departmentKey: "gulf_university",                   equivalentRate: 75.20, type: "high_school", track: "scientific" },
    { code: "IM-MKT-GUST-L", collegeKey: "marketing", departmentKey: "gulf_university",                   equivalentRate: 78.16, type: "high_school", track: "literary"   },
    { code: "IM-MKT-AUM-S",  collegeKey: "marketing", departmentKey: "american_university_middle_east",   equivalentRate: 75.82, type: "high_school", track: "scientific" },
    { code: "IM-MKT-AUM-L",  collegeKey: "marketing", departmentKey: "american_university_middle_east",   equivalentRate: 78.40, type: "high_school", track: "literary"   },
    { code: "IM-MKT-AIU-S",  collegeKey: "marketing", departmentKey: "american_international_university", equivalentRate: 76.52, type: "high_school", track: "scientific" },
    { code: "IM-MKT-AIU-L",  collegeKey: "marketing", departmentKey: "american_international_university", equivalentRate: 78.00, type: "high_school", track: "literary"   },

    // English Language
    { code: "IM-ENG-AUK-S",  collegeKey: "english_language", departmentKey: "american_university_kuwait", equivalentRate: 78.13, type: "high_school", track: "scientific" },
    { code: "IM-ENG-AUK-L",  collegeKey: "english_language", departmentKey: "american_university_kuwait", equivalentRate: 80.53, type: "high_school", track: "literary"   },
    { code: "IM-ENG-GUST-S", collegeKey: "english_language", departmentKey: "gulf_university",            equivalentRate: 78.55, type: "high_school", track: "scientific" },
    { code: "IM-ENG-GUST-L", collegeKey: "english_language", departmentKey: "gulf_university",            equivalentRate: 80.32, type: "high_school", track: "literary"   },

    // Social & Behavioral Sciences
    { code: "IM-SOC-AUK-S", collegeKey: "social_behavioral_sciences", departmentKey: "american_university_kuwait", equivalentRate: 79.85, type: "high_school", track: "scientific" },
    { code: "IM-SOC-AUK-L", collegeKey: "social_behavioral_sciences", departmentKey: "american_university_kuwait", equivalentRate: 80.00, type: "high_school", track: "literary"   },

    // Human Resources
    { code: "IM-HRM-AUK-S", collegeKey: "human_resources", departmentKey: "american_university_kuwait",      equivalentRate: 75.00, type: "high_school", track: "scientific" },
    { code: "IM-HRM-AUK-L", collegeKey: "human_resources", departmentKey: "american_university_kuwait",      equivalentRate: 78.00, type: "high_school", track: "literary"   },
    { code: "IM-HRM-AUM-S", collegeKey: "human_resources", departmentKey: "american_university_middle_east", equivalentRate: 78.02, type: "high_school", track: "scientific" },
    { code: "IM-HRM-AUM-L", collegeKey: "human_resources", departmentKey: "american_university_middle_east", equivalentRate: 80.57, type: "high_school", track: "literary"   },

    // Law (Kuwait International Law School)
    { code: "IM-LAW-KILAW-S", collegeKey: "law", departmentKey: "kuwait_international_law_school", equivalentRate: 91.35, type: "high_school", track: "scientific" },
    { code: "IM-LAW-KILAW-L", collegeKey: "law", departmentKey: "kuwait_international_law_school", equivalentRate: 91.27, type: "high_school", track: "literary"   },

    // Business Administration
    { code: "IM-BUS-GUST-S", collegeKey: "business_admin", departmentKey: "gulf_university", equivalentRate: 78.16, type: "high_school", track: "scientific" },
    { code: "IM-BUS-GUST-L", collegeKey: "business_admin", departmentKey: "gulf_university", equivalentRate: 80.16, type: "high_school", track: "literary"   },

    // Event Management
    { code: "IM-EVT-AU-S", collegeKey: "event_management", departmentKey: "australian_university", equivalentRate: 75.00, type: "high_school", track: "scientific" },
    { code: "IM-EVT-AU-L", collegeKey: "event_management", departmentKey: "australian_university", equivalentRate: 80.33, type: "high_school", track: "literary"   },

    // Aviation Operations
    { code: "IM-AVO-AU-S", collegeKey: "aviation_operations", departmentKey: "australian_university", equivalentRate: 78.00, type: "high_school", track: "scientific" },
    { code: "IM-AVO-AU-L", collegeKey: "aviation_operations", departmentKey: "australian_university", equivalentRate: 80.96, type: "high_school", track: "literary"   },

    // Banking & Finance
    { code: "IM-BNF-AU-S", collegeKey: "banking_finance", departmentKey: "australian_university", equivalentRate: 78.12, type: "high_school", track: "scientific" },
    { code: "IM-BNF-AU-L", collegeKey: "banking_finance", departmentKey: "australian_university", equivalentRate: 80.00, type: "high_school", track: "literary"   },

    // Information Systems & Technology
    { code: "IM-IST-AUM-S", collegeKey: "information_systems_technology", departmentKey: "american_university_middle_east", equivalentRate: 86.59, type: "high_school", track: "scientific" },

    // AI Sciences & Applications (new in the 2026/2027 rates PDF; scientific only)
    { code: "IM-AIS-AUM-S", collegeKey: "ai_science_applications", departmentKey: "american_university_middle_east", equivalentRate: 85.08, type: "high_school", track: "scientific" },

    // Biotechnology
    { code: "IM-BTC-AIU-S", collegeKey: "biotechnology", departmentKey: "american_international_university", equivalentRate: 83.93, type: "high_school", track: "scientific" },

    // Math Education
    { code: "IM-MED-AIU-S", collegeKey: "math_education", departmentKey: "american_international_university", equivalentRate: 89.44, type: "high_school", track: "scientific" },

    // Interior Design
    { code: "IM-IND-AIU-S", collegeKey: "interior_design", departmentKey: "american_international_university", equivalentRate: 83.43, type: "high_school", track: "scientific" },
    { code: "IM-IND-AIU-L", collegeKey: "interior_design", departmentKey: "american_international_university", equivalentRate: 84.25, type: "high_school", track: "literary"   },

    // Logistics Supply Chain
    { code: "IM-LSC-AIU-S", collegeKey: "logistics_supply_chain", departmentKey: "american_international_university", equivalentRate: 78.01, type: "high_school", track: "scientific" },
    { code: "IM-LSC-AIU-L", collegeKey: "logistics_supply_chain", departmentKey: "american_international_university", equivalentRate: 81.57, type: "high_school", track: "literary"   },

    // Entrepreneurship (new in the 2026/2027 rates PDF; both tracks)
    { code: "IM-ENT-AIU-S", collegeKey: "entrepreneurship", departmentKey: "american_international_university", equivalentRate: 78.47, type: "high_school", track: "scientific" },
    { code: "IM-ENT-AIU-L", collegeKey: "entrepreneurship", departmentKey: "american_international_university", equivalentRate: 80.00, type: "high_school", track: "literary"   },

    // English Education
    { code: "IM-EED-AIU-S", collegeKey: "english_education", departmentKey: "american_international_university", equivalentRate: 85.00, type: "high_school", track: "scientific" },
    { code: "IM-EED-AIU-L", collegeKey: "english_education", departmentKey: "american_international_university", equivalentRate: 86.36, type: "high_school", track: "literary"   },
];

// ── Colleges (top-level specialties) ─────────────────────────────────

export const colleges: College[] = [
    // Engineering (scientific only)
    { key: "architectural_engineering",       emoji: "🏢",  track: "scientific", weights: engineeringWeights, departments: departments.filter((d) => d.collegeKey === "architectural_engineering") },
    { key: "biomedical_engineering",          emoji: "🦾",  track: "scientific", weights: engineeringWeights, departments: departments.filter((d) => d.collegeKey === "biomedical_engineering") },
    { key: "chemical_engineering",            emoji: "🏭",  track: "scientific", weights: engineeringWeights, departments: departments.filter((d) => d.collegeKey === "chemical_engineering") },
    { key: "civil_engineering",               emoji: "🏗️",  track: "scientific", weights: engineeringWeights, departments: departments.filter((d) => d.collegeKey === "civil_engineering") },
    { key: "computer_engineering",            emoji: "👨‍💻", track: "scientific", weights: engineeringWeights, departments: departments.filter((d) => d.collegeKey === "computer_engineering") },
    { key: "electrical_computer_engineering", emoji: "🎛️",  track: "scientific", weights: engineeringWeights, departments: departments.filter((d) => d.collegeKey === "electrical_computer_engineering") },
    { key: "electrical_engineering",          emoji: "⚡",  track: "scientific", weights: engineeringWeights, departments: departments.filter((d) => d.collegeKey === "electrical_engineering") },
    { key: "electronics_comm_engineering",    emoji: "📡",  track: "scientific", weights: engineeringWeights, departments: departments.filter((d) => d.collegeKey === "electronics_comm_engineering") },
    { key: "systems_engineering",             emoji: "⚙️",  track: "scientific", weights: engineeringWeights, departments: departments.filter((d) => d.collegeKey === "systems_engineering") },

    // Non-engineering (no track on college; filtering happens at department level)
    { key: "accounting",                      emoji: "📊",  weights: defaultWeights, departments: departments.filter((d) => d.collegeKey === "accounting") },
    { key: "ai_science_applications",         emoji: "🤖",  weights: defaultWeights, departments: departments.filter((d) => d.collegeKey === "ai_science_applications") },
    { key: "aviation_operations",             emoji: "🛫",  weights: defaultWeights, departments: departments.filter((d) => d.collegeKey === "aviation_operations") },
    { key: "banking_finance",                 emoji: "💵",  weights: defaultWeights, departments: departments.filter((d) => d.collegeKey === "banking_finance") },
    { key: "biotechnology",                   emoji: "🧬",  weights: defaultWeights, departments: departments.filter((d) => d.collegeKey === "biotechnology") },
    { key: "business_admin",                  emoji: "💸",  weights: defaultWeights, departments: departments.filter((d) => d.collegeKey === "business_admin") },
    { key: "computer_science",                emoji: "💻",  weights: defaultWeights, departments: departments.filter((d) => d.collegeKey === "computer_science") },
    { key: "economics",                       emoji: "📉",  weights: defaultWeights, departments: departments.filter((d) => d.collegeKey === "economics") },
    { key: "english_education",               emoji: "🎓",  weights: defaultWeights, departments: departments.filter((d) => d.collegeKey === "english_education") },
    { key: "english_language",                emoji: "📚",  weights: defaultWeights, departments: departments.filter((d) => d.collegeKey === "english_language") },
    { key: "entrepreneurship",                emoji: "🚀",  weights: defaultWeights, departments: departments.filter((d) => d.collegeKey === "entrepreneurship") },
    { key: "event_management",                emoji: "📅",  weights: defaultWeights, departments: departments.filter((d) => d.collegeKey === "event_management") },
    { key: "finance",                         emoji: "💰",  weights: defaultWeights, departments: departments.filter((d) => d.collegeKey === "finance") },
    { key: "graphic_design",                  emoji: "🎨",  weights: defaultWeights, departments: departments.filter((d) => d.collegeKey === "graphic_design") },
    { key: "human_resources",                 emoji: "🧑‍🤝‍🧑", weights: defaultWeights, departments: departments.filter((d) => d.collegeKey === "human_resources") },
    { key: "information_systems_technology",  emoji: "🖥️",  weights: defaultWeights, departments: departments.filter((d) => d.collegeKey === "information_systems_technology") },
    { key: "interior_design",                 emoji: "🛋️",  weights: defaultWeights, departments: departments.filter((d) => d.collegeKey === "interior_design") },
    { key: "international_relations",         emoji: "🌍",  weights: defaultWeights, departments: departments.filter((d) => d.collegeKey === "international_relations") },
    { key: "law",                             emoji: "⚖️",  weights: defaultWeights, departments: departments.filter((d) => d.collegeKey === "law") },
    { key: "logistics_supply_chain",          emoji: "📦",  weights: defaultWeights, departments: departments.filter((d) => d.collegeKey === "logistics_supply_chain") },
    { key: "management",                      emoji: "💼",  weights: defaultWeights, departments: departments.filter((d) => d.collegeKey === "management") },
    { key: "management_info_systems",         emoji: "🗂️",  weights: defaultWeights, departments: departments.filter((d) => d.collegeKey === "management_info_systems") },
    { key: "marketing",                       emoji: "📢",  weights: defaultWeights, departments: departments.filter((d) => d.collegeKey === "marketing") },
    { key: "math_education",                  emoji: "🔢",  weights: defaultWeights, departments: departments.filter((d) => d.collegeKey === "math_education") },
    { key: "media",                           emoji: "🎥",  weights: defaultWeights, departments: departments.filter((d) => d.collegeKey === "media") },
    { key: "social_behavioral_sciences",      emoji: "🧠",  weights: defaultWeights, departments: departments.filter((d) => d.collegeKey === "social_behavioral_sciences") },
];

// ── Helpers ─────────────────────────────────────────────────────────

export function getCollegeMinRate(
    collegeKey: string,
    _gender?: Gender,
    track?: Track | null,
): number {
    const collegeDepts = departments.filter(
        (d) => d.collegeKey === collegeKey && (!track || d.track === track),
    );
    if (collegeDepts.length === 0) return 0;
    return Math.min(...collegeDepts.map((d) => d.equivalentRate));
}

export function getRequiredFields(weights: CollegeWeights): string[] {
    const fields: string[] = ["high_school"];
    if (weights.englishWeight > 0) fields.push("english");
    if (weights.mathWeight > 0) fields.push("math");
    if (weights.arabicWeight > 0) fields.push("arabic");
    if (weights.chemistryWeight > 0) fields.push("chemistry");
    return fields;
}

export function getCollegeSpecialties(
    collegeKey: string,
    _gender?: Gender,
    track?: Track | null,
): Department[] {
    const rows = departments.filter(
        (d) => d.collegeKey === collegeKey && d.departmentKey !== null && (!track || d.track === track),
    );
    const seen = new Set<string>();
    return rows.filter((d) => {
        const k = d.departmentKey as string;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
    });
}

export function hasSelectableSpecialties(
    collegeKey: string,
    _gender?: Gender,
    track?: Track | null,
): boolean {
    return getCollegeSpecialties(collegeKey, undefined, track).length > 0;
}

/* ── Internal Missions equivalent-rate calculation (isolated) ────────────
 * Owned by this module so IM math is decoupled from every other section.
 */
export function calculateEquivalentRate(weights: CollegeWeights, scores: Scores): number {
    if (weights.admissionType === "high_school") {
        return scores.high_school;
    }
    const rate =
        (scores.high_school * weights.highSchoolWeight) / 100 +
        ((scores.english || 0) * weights.englishWeight) / 100 +
        ((scores.math || 0) * weights.mathWeight) / 100 +
        ((scores.arabic || 0) * weights.arabicWeight) / 100 +
        ((scores.chemistry || 0) * weights.chemistryWeight) / 100;
    return Math.round(rate * 100) / 100;
}

export function meetsMinHighSchool(weights: CollegeWeights, scores: Scores): boolean {
    if (weights.minHighSchool === null) return true;
    return scores.high_school >= weights.minHighSchool;
}

export function meetsMinEnglish(weights: CollegeWeights, scores: Scores): boolean {
    if (weights.minEnglish === null) return true;
    return (scores.english || 0) >= weights.minEnglish;
}

/* ── Per-section public API (consumed via the registry) ─────────────────
 * IM owns its track percentages, threshold, and label key.
 */

/** IM surfaces no minimum-to-apply anywhere: the only published concept is
 *  last year's lowest accepted value (أقل معدل), carried per department on
 *  `equivalentRate`. No track-level HS gate. */
export function getTrackMinHighSchool(_track: Track | null | undefined): number | null {
    return null;
}

/** IM has no hard gates at all — every threshold shown is guidance. */
export function isCollegeMinHardGate(): boolean {
    return false;
}

export function getDepartmentThreshold(dept: Department): number {
    return dept.equivalentRate;
}

export function getDisplayedMinRate(
    _dept: Department | null,
    _track: Track | null | undefined,
    _weights: CollegeWeights,
    fallbackMinRate: number,
): number {
    return fallbackMinRate;
}

export function getDisplayedMinRateLabelKey(weights: CollegeWeights): string {
    return weights.admissionType === "high_school"
        ? "dream_university_im_noneng_last_year_hs"
        : "dream_university_im_eng_last_year_rate";
}

export function getColleges(_gender?: Gender, _track?: Track | null): College[] {
    return colleges;
}

export function getDepartmentsForFilters(_gender?: Gender, _track?: Track | null): Department[] {
    return departments;
}

/* ── Step alerts (owned by Internal Missions, rendered by shared steps) ──────
 * All IM alert wording, percentages, and show/hide rules live here so IM's
 * alerts stay fully isolated from every other section. The shared step
 * components only render the descriptors these functions return.
 */
export interface ImAlertLine {
    key: string;
    params: Record<string, number>;
}

/** Persistent score-step alert: last year's lowest accepted value only.
 *  Engineering's value is an equivalent rate; non-engineering's is the HS %
 *  accepted last year — the wording differs accordingly. There is no
 *  minimum-to-apply line: أقل معدل is the single concept IM surfaces. */
export function getImScoreAlertLines(dept: Department | null | undefined): ImAlertLine[] {
    if (!dept || dept.equivalentRate <= 0) {
        return [];
    }
    return [
        {
            key: dept.type === "equivalent"
                ? "dream_university_im_eng_alert_lastyear"
                : "dream_university_im_noneng_alert_lastyear",
            params: { rate: dept.equivalentRate },
        },
    ];
}

/** Soft inline note under the HS input when the entered grade is below last
 *  year's lowest accepted HS (guidance only — never blocks calculation).
 *  Engineering rows admit on an equivalent rate, so the raw HS input is not
 *  compared there. */
export function getImHsBelowAcceptedLine(
    dept: Department | null | undefined,
    enteredHs: number,
): ImAlertLine | null {
    if (
        !dept
        || dept.type !== "high_school"
        || dept.equivalentRate <= 0
        || isNaN(enteredHs)
        || enteredHs <= 0
        || enteredHs >= dept.equivalentRate
    ) {
        return null;
    }
    return {
        key: "dream_university_im_hs_below_accepted_inline",
        params: { entered: enteredHs, rate: dept.equivalentRate },
    };
}

/** Results-step failure lines (calculated rate below the target): last-year
 *  accepted value only, with the same per-type wording as the card. */
export function getImResultsFailureLines(
    dept: Department | null | undefined,
    lastYearRate: number,
): ImAlertLine[] {
    if (!dept || lastYearRate <= 0) {
        return [];
    }
    return [
        {
            key: dept.type === "equivalent"
                ? "dream_university_im_eng_alert_lastyear"
                : "dream_university_im_noneng_alert_lastyear",
            params: { rate: lastYearRate },
        },
    ];
}

/** Registry-facing wrappers (uniform StepAlert contract). */
export function getScoreInfoAlert(
    _college: College,
    department: Department | null,
    _track: Track | null | undefined,
    _collegeMinHs: number | null,
    _enteredHs: number,
): StepAlert | null {
    const lines = getImScoreAlertLines(department);
    if (lines.length === 0) {
        return null;
    }
    // Guidance only — last year's accepted value is informational, so the
    // alert is always an amber heads-up and never blocks calculation.
    return { severity: "warning", lines };
}

export function getResultsFailureLines(
    _college: College,
    department: Department | null,
    _track: Track | null | undefined,
    _trackMinHs: number | null,
    minRate: number,
): StepAlertLine[] {
    return getImResultsFailureLines(department, minRate);
}

// ── IM-specific institution registry & navigation helpers ──────────────────
// Institution-first cascade: Track → Institution → Discipline → Specialty.
// Mirrors FM (Track → Country → Discipline → Specialty) but with university-
// internal institutions instead of countries. Fully isolated from FM data.

export interface ImInstitution {
    key: string;
    emoji: string;
    order: number;
    /** Official university logo (served from `public/`). Rendered in place of
     *  the emoji on the institution picker when present. Sourced from each
     *  university's official website (GUST: official logo via Wikimedia Commons,
     *  as gust.edu.kw serves no static logo asset). */
    logo?: string;
}

/** The 7 internal-missions institutions, in PDF order. Each carries its
 *  official logo; `emoji` is the fallback if a logo fails to load. */
export const imInstitutions: ImInstitution[] = [
    { key: "american_university_kuwait",        emoji: "🇺🇸", order: 1, logo: "/images/universities/american_university_kuwait.png" },
    { key: "gulf_university",                   emoji: "🌊", order: 2, logo: "/images/universities/gulf_university.jpg" },
    { key: "american_university_middle_east",   emoji: "🏛️", order: 3, logo: "/images/universities/american_university_middle_east.svg" },
    { key: "american_international_university", emoji: "🌐", order: 4, logo: "/images/universities/american_international_university.png" },
    { key: "kuwait_college_science_technology", emoji: "🔬", order: 5, logo: "/images/universities/kuwait_college_science_technology.png" },
    { key: "australian_university",             emoji: "🇦🇺", order: 6, logo: "/images/universities/australian_university.png" },
    { key: "kuwait_international_law_school",   emoji: "⚖️", order: 7, logo: "/images/universities/kuwait_international_law_school.jpg" },
];

export function getImInstitutionByKey(key: string): ImInstitution | undefined {
    return imInstitutions.find((i) => i.key === key);
}

// ── Discipline groupings ───────────────────────────────────────────────────

export type ImDisciplineKey =
    | "engineering"
    | "computer_science_it"
    | "business_management"
    | "design_media"
    | "law"
    | "education_humanities"
    | "sciences_aviation";

export interface ImDiscipline {
    key: ImDisciplineKey;
    emoji: string;
    order: number;
}

export const imDisciplines: ImDiscipline[] = [
    { key: "engineering",           emoji: "🛠️", order: 1 },
    { key: "computer_science_it",   emoji: "💻", order: 2 },
    { key: "business_management",   emoji: "💼", order: 3 },
    { key: "design_media",          emoji: "🎨", order: 4 },
    { key: "law",                   emoji: "⚖️", order: 5 },
    { key: "education_humanities",  emoji: "📚", order: 6 },
    { key: "sciences_aviation",     emoji: "🔬", order: 7 },
];

/** Map every IM specialty (collegeKey) → its discipline group. */
const specialtyToImDiscipline: Record<string, ImDisciplineKey> = {
    architectural_engineering:        "engineering",
    biomedical_engineering:           "engineering",
    chemical_engineering:             "engineering",
    civil_engineering:                "engineering",
    computer_engineering:             "engineering",
    electrical_computer_engineering:  "engineering",
    electrical_engineering:           "engineering",
    electronics_comm_engineering:     "engineering",
    systems_engineering:              "engineering",

    ai_science_applications:          "computer_science_it",
    computer_science:                 "computer_science_it",
    information_systems_technology:   "computer_science_it",
    management_info_systems:          "computer_science_it",

    accounting:                       "business_management",
    banking_finance:                  "business_management",
    business_admin:                   "business_management",
    economics:                        "business_management",
    entrepreneurship:                 "business_management",
    event_management:                 "business_management",
    finance:                          "business_management",
    human_resources:                  "business_management",
    logistics_supply_chain:           "business_management",
    management:                       "business_management",
    marketing:                        "business_management",

    graphic_design:                   "design_media",
    interior_design:                  "design_media",
    media:                            "design_media",

    law:                              "law",

    english_education:                "education_humanities",
    math_education:                   "education_humanities",
    english_language:                 "education_humanities",
    social_behavioral_sciences:       "education_humanities",
    international_relations:          "education_humanities",

    biotechnology:                    "sciences_aviation",
    aviation_operations:              "sciences_aviation",
};

export function getImDisciplineForSpecialty(specialtyKey: string): ImDisciplineKey | undefined {
    return specialtyToImDiscipline[specialtyKey];
}

// ── Navigation helpers (consumed by Choose Im* step components) ───────────

/** Institutions that have at least one specialty for the given track.
 *  PDF order preserved. */
export function getImInstitutionsForTrack(track: Track | null | undefined): ImInstitution[] {
    const matchesTrack = (d: Department): boolean =>
        !track || !d.track || d.track === track;
    return imInstitutions
        .filter((i) => departments.some((d) => d.departmentKey === i.key && matchesTrack(d)))
        .sort((a, b) => a.order - b.order);
}

/** Disciplines available in the chosen institution + track. */
export function getImDisciplinesForInstitution(
    institutionKey: string,
    track: Track | null | undefined,
): ImDiscipline[] {
    const matchesTrack = (d: Department): boolean =>
        !track || !d.track || d.track === track;
    const present = new Set<ImDisciplineKey>();
    for (const d of departments) {
        if (d.departmentKey !== institutionKey) continue;
        if (!matchesTrack(d)) continue;
        const disc = specialtyToImDiscipline[d.collegeKey];
        if (disc) present.add(disc);
    }
    return imDisciplines
        .filter((dx) => present.has(dx.key))
        .sort((a, b) => a.order - b.order);
}

/** Department rows for a given (institution, discipline, track). */
export function getImSpecialtiesForInstitutionAndDiscipline(
    institutionKey: string,
    discipline: ImDisciplineKey,
    track: Track | null | undefined,
): Department[] {
    const matchesTrack = (d: Department): boolean =>
        !track || !d.track || d.track === track;
    return departments
        .filter(
            (d) =>
                d.departmentKey === institutionKey
                && matchesTrack(d)
                && specialtyToImDiscipline[d.collegeKey] === discipline,
        );
}
